import {
  RawMetalData,
  DailyMetalData,
  MetalFilters,
  MetalStats,
  MetalChartData
} from '../../types/metal';
import { supabase } from '../supabase/client';

const CUSTOM_API_PROXY = '/api/metal-data';

/** Bearer header with the current Supabase session, required by the authenticated proxy. */
async function authHeader(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Fetch metal production data via our authenticated proxy.
 */
export async function getMetalData(filters: MetalFilters): Promise<RawMetalData[]> {
  const { fromDate, toDate, compID } = filters;

  // NOTE: third-party CORS proxy fallbacks (cors.sh, allorigins) were removed —
  // they tunnelled internal production data through external services. Use only
  // our own authenticated proxy.
  console.log('🔥 Metal Service: Trying custom API proxy...');
  const params = new URLSearchParams({
    fromDate,
    toDate,
    compID: compID.toString()
  });

  const response = await fetch(`${CUSTOM_API_PROXY}?${params}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(await authHeader()),
    },
    signal: (() => {
      const controller = new AbortController();
      setTimeout(() => controller.abort(), 120000);
      return controller.signal;
    })()
  });

  if (!response.ok) {
    throw new Error(`Custom API proxy error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  console.log('🔥 Metal Service: Custom API proxy success, data length:', Array.isArray(data) ? data.length : 'not array');
  return data;
}

/**
 * Transform raw metal API data to structured daily data
 */
export function transformMetalToDaily(rawData: RawMetalData[]): DailyMetalData[] {
  return rawData.map(item => ({
    date: item.DocDate.split('T')[0], // Extract date part only
    processes: {
      phoi: {
        labor: item.CPhoi || 0,
        weight: item.KgPhoi || 0,
        productivity: item.NSPhoi || 0,
      },
      han: {
        labor: item.CHan || 0,
        weight: item.KgHan || 0,
        productivity: item.NSHan || 0,
      },
      son: {
        labor: item.CSon || 0,
        weight: item.KgSon || 0,
        productivity: item.NSSon || 0,
      },
      kiemTran: {
        labor: item.CKCS_Tran || 0,
        weight: item.KgKCS_Tran || 0,
        productivity: item.NSKCS_Tran || 0,
      },
      kiemDan: {
        labor: item.CKCS_Dan || 0,
        weight: item.KgKCS_Dan || 0,
        productivity: item.NSKCS_Dan || 0,
      },
      thanhPham: {
        labor: item.CLRap || 0,
        weight: item.KgLRap || 0,
        productivity: item.NSLRap || 0,
      },
    },
    // Finished product data (M3 carton and container count)
    m3Carton: item.M3DGoi || 0,           // M3 carton from API
    containerCount: item.QtyContDGoi || 0, // Container count from API
  }));
}

/**
 * Calculate statistics for metal production data
 */
export function calculateMetalStats(dailyData: DailyMetalData[]): MetalStats {
  if (dailyData.length === 0) {
    return {
      totalDays: 0,
      workingDays: 0,
      totalLabor: 0,
      totalWeight: 0,
      avgLaborPerDay: 0,
      avgWeightPerDay: 0,
      avgProductivityPerDay: 0,
      maxWeight: 0,
      minWeight: 0,
      processStats: {},
    };
  }

  // Working days: days where at least one process has labor > 0
  const workingDays = dailyData.filter(day => {
    const processes = Object.values(day.processes);
    return processes.some(process => process.labor > 0);
  });

  // Calculate totals
  let totalLabor = 0;
  let totalWeight = 0;
  let maxWeight = 0;
  let minWeight = Infinity;

  const processNames = ['phoi', 'han', 'son', 'kiemTran', 'kiemDan', 'thanhPham'];
  const processStats: any = {};

  // Initialize process stats
  processNames.forEach(processName => {
    processStats[processName] = {
      totalLabor: 0,
      totalWeight: 0,
      avgLabor: 0,
      avgWeight: 0,
      avgProductivity: 0,
    };
  });

  // Calculate stats for working days
  workingDays.forEach(day => {
    processNames.forEach(processName => {
      const process = day.processes[processName as keyof typeof day.processes];
      processStats[processName].totalLabor += process.labor;
      processStats[processName].totalWeight += process.weight;
      
      totalLabor += process.labor;
      totalWeight += process.weight;
      
      if (process.weight > maxWeight) maxWeight = process.weight;
      if (process.weight < minWeight && process.weight > 0) minWeight = process.weight;
    });
  });

  // Calculate averages
  const workingDaysCount = workingDays.length;
  processNames.forEach(processName => {
    const stats = processStats[processName];
    stats.avgLabor = workingDaysCount > 0 ? stats.totalLabor / workingDaysCount : 0;
    stats.avgWeight = workingDaysCount > 0 ? stats.totalWeight / workingDaysCount : 0;
    stats.avgProductivity = stats.totalLabor > 0 ? stats.totalWeight / stats.totalLabor : 0;
  });

  if (minWeight === Infinity) minWeight = 0;

  return {
    totalDays: dailyData.length,
    workingDays: workingDaysCount,
    totalLabor,
    totalWeight,
    avgLaborPerDay: workingDaysCount > 0 ? totalLabor / workingDaysCount : 0,
    avgWeightPerDay: workingDaysCount > 0 ? totalWeight / workingDaysCount : 0,
    avgProductivityPerDay: totalLabor > 0 ? totalWeight / totalLabor : 0,
    maxWeight,
    minWeight,
    processStats,
  };
}

/**
 * Transform daily data to chart data format
 */
export function getMetalChartData(dailyData: DailyMetalData[], metric: 'labor' | 'weight' | 'productivity'): MetalChartData[] {
  return dailyData.map(day => ({
    date: day.date,
    phoi: day.processes.phoi[metric] || 0,
    han: day.processes.han[metric] || 0,
    son: day.processes.son[metric] || 0,
    kiemTran: day.processes.kiemTran[metric] || 0,
    kiemDan: day.processes.kiemDan[metric] || 0,
    thanhPham: day.processes.thanhPham[metric] || 0,
  }));
}